import { useCallback, useEffect, useRef, useState } from 'react'

import { graphql } from '../../services/graphql'
import { User } from '../../state/models/users'

const PAGE_SIZE = 50

export function useDatasourceUser({
  search,
  space,
}: {
  search: string
  space: string | null
}) {
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const generation = useRef(0)
  const usersRef = useRef<User[]>([])
  usersRef.current = users

  useEffect(() => {
    const myGen = ++generation.current
    setUsers([])
    setTotal(0)
    setHasMore(true)
    setLoading(true)

    const fetchInitial = async () => {
      try {
        const res = await graphql.queries.getUsers({
          search,
          space,
          skip: 0,
          limit: PAGE_SIZE,
        })
        if (myGen !== generation.current) {
          return
        }
        setUsers(res.userList)
        setTotal(res.userCount)
        setHasMore(res.userList.length === PAGE_SIZE)
      } finally {
        if (myGen === generation.current) {
          setLoading(false)
        }
      }
    }

    fetchInitial()
  }, [search, space])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) {
      return
    }
    const myGen = generation.current
    const skip = usersRef.current.length
    setLoading(true)
    try {
      const res = await graphql.queries.getUsers({
        search,
        space,
        skip,
        limit: PAGE_SIZE,
      })
      if (myGen !== generation.current) {
        return
      }
      setUsers((prev) => [...prev, ...res.userList])
      setTotal(res.userCount)
      setHasMore(res.userList.length === PAGE_SIZE)
    } finally {
      if (myGen === generation.current) {
        setLoading(false)
      }
    }
  }, [search, space, loading, hasMore])

  return { users, loadMore, loading, total, hasMore }
}
